/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  fetchAllBlogs,
  fetchCategories,
  fetchBlogsByCategoryId,
  fetchBlogsCount,
} from "@/lib/api/blog";
import { BlogPost, Category } from "@/types/blog";
import { calculateReadTime } from "@/lib/utils";

export const useBlogsData = (itemsPerPage = 9) => {
  const { language } = useLanguage();

  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("All");
  const [totalPosts, setTotalPosts] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await fetchCategories(language);
        const filtered = data.filter((cat) => cat.title !== "Untitled");
        setCategories(filtered);
      } catch (err: any) {
        setError(err.message);
      }
    };
    loadCategories();
  }, [language]);

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        setLoading(true);
        setError(null);

        const offset = (currentPage - 1) * itemsPerPage;

        const countData =
          selectedCategoryId === "All"
            ? await fetchBlogsCount()
            : await fetchBlogsCount(selectedCategoryId);

        setTotalPosts(countData.totalPosts);

        const blogData =
          selectedCategoryId === "All"
            ? await fetchAllBlogs(offset, itemsPerPage, language)
            : await fetchBlogsByCategoryId(
                selectedCategoryId,
                offset,
                itemsPerPage,
                language
              );

        const withReadTime = blogData.map((blog) => ({
          ...blog,
          readTime: calculateReadTime(blog.content || ""),
        }));

        setBlogs(withReadTime);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, [selectedCategoryId, currentPage, language, itemsPerPage]);

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setCurrentPage(1);
  };

  return {
    blogs,
    categories,
    loading,
    error,
    selectedCategoryId,
    currentPage,
    totalPosts,
    setCurrentPage,
    handleCategoryChange,
    setSelectedCategoryId,
    setBlogs,
    setCategories,
  };
}
